/* eslint-disable react-hooks/set-state-in-effect -- this numeric input intentionally mirrors formatted external value changes into local edit state. */
import {useCallback, useEffect, useRef, useState} from "react";
import type {INumericFieldSettingsDefinition} from "../../../../../data/models/field/datatypes/Numeric";
import {useAppStore} from "../../../../../StateManager";
import {Functions} from "../../../../../utils/function/Functions";
import {HorizontalAlignment, VerticalAlignment} from "../../../../../utils/dom/DomUtils";
import {KeyboardListener} from "../../../../../utils/interaction/key/KeyboardListener";
import {StringUtils} from "../../../../../utils/data/string/StringUtils";
import {InfoBubbleV5} from "../../../button/InfoBubbleV5";
import ErrorInfoIcon from "../../../icons/errorInfo.svg?react";
import {FieldDataTypes} from "../../../../../data/models/field/FieldDataTypes";
import {FieldDataType} from "../../../../../generated/api/base";

export const pageNumberClassName = "pageNumber";

interface INumberInputV5Props {
	readonly value?: number;
	readonly onInput?: (value: number | string) => void;
	readonly onChange?: (value: number) => void;
	readonly onClick?: () => void;
	readonly onBlur?: () => void;
	readonly decimals?: number;
	readonly disabled?: boolean;
	readonly className?: string;
	readonly autoFocus?: boolean;
	readonly dataTypeSettings?: INumericFieldSettingsDefinition;
	readonly caretPosition?: number;
	readonly onlyNaturalNumbers?: boolean;
	readonly getErrorMessage?: (inputString: string, dataTypeSettings?: INumericFieldSettingsDefinition, onlyNaturalNumbers?: boolean) => string;
	readonly errorMessageVerticalAlignment?: VerticalAlignment;
	readonly errorMessageHorizontalAlignment?: HorizontalAlignment;
	readonly errorMessageOffsetY?: number;
	readonly errorMessageOffsetX?: number;
}

const getInitialEditingValue = (value: number, dataTypeSettings: INumericFieldSettingsDefinition, decimals: number): string => {
	// value is undefined or null or "", but not 0
	let returnValue = getInitialValidValue(value, dataTypeSettings, decimals);

	return FieldDataTypes.map[FieldDataType.Numeric].formatter(returnValue, dataTypeSettings);
};

const getInitialValidValue = (value: number, dataTypeSettings: INumericFieldSettingsDefinition, decimals: number): string => {
	// value is undefined or null or "", but not 0
	if (value !== 0 && !value) {
		return "";
	}

	if (dataTypeSettings?.formatting === "percentage") {
		return (Number(value) * 100).toFixed(decimals);
	}

	return String(value);
};

const getEditingValueAsNumber = (value: string) => {
	if (!value) {
		return 0;
	}
	return parseFloat(value);
};

const getLastValidValue = (value: string, dataTypeSettings: INumericFieldSettingsDefinition) => {
	if (!value) {
		return null; // no record -> we send null to BE
	}

	const inputValue = parseFloat(value);
	const divident = dataTypeSettings?.formatting === "percentage" ? 100 : 1;
	const finalValue = inputValue === null ? null : inputValue / divident; // if a user wants to delete the value and leave it empty, we need to send null (value / divident) becomes 0 instead of null

	return isNaN(finalValue) ? 0 : finalValue;
};

const getAPIUpdateNumber = (inputString: string) => {
	let valueAsString = inputString;
	if (StringUtils.trim(valueAsString) === "") {
		// Note: very important to return null instead of undefined.
		// undefined doesn't work because in XHRLoader JSON.stringify converts {f35: undefined} -> {}
		// causing a bug that when a numberinput is cleared it's not saved.
		return null;
	}

	valueAsString = valueAsString
		.replace(/,/g, "") // remove all commas
		.replace(/%/g, "") // remove % sign
		.replace(/\$/g, ""); // remove $ sign

	const valueAsFloat = parseFloat(valueAsString);

	return valueAsFloat;
};

export const NumberInputV5 = (props: INumberInputV5Props) => {
	const {
		value = null,
		onInput = Functions.emptyFunction,
		onChange = Functions.emptyFunction,
		onBlur = Functions.emptyFunction,
		onClick = Functions.emptyFunction,
		decimals = 2,
		disabled,
		className = "",
		autoFocus = true,
		dataTypeSettings,
		caretPosition = 0,
		onlyNaturalNumbers = false,
		errorMessageVerticalAlignment = VerticalAlignment.topOuter,
		errorMessageHorizontalAlignment = HorizontalAlignment.right,
		errorMessageOffsetY = 8,
		errorMessageOffsetX = 0,
	} = props;
	const _ref = useRef<HTMLInputElement>(null);
	const _errorIconRef = useRef<HTMLDivElement>(null);
	const _lastValidValue = useRef<number>(getLastValidValue(getInitialValidValue(value, dataTypeSettings, decimals), dataTypeSettings));
	const _hasTriggeredChange = useRef<boolean>(false);

	const appState = useAppStore((state) => state.appState);
	const getErrorMessage = props.getErrorMessage ?? appState.actions.getNumberInputErrorMessage;
	const [editingValue, setEditingValue] = useState<string>(getInitialEditingValue(value, dataTypeSettings, decimals));
	const errorMessage = getErrorMessage(editingValue, dataTypeSettings, onlyNaturalNumbers);

	useEffect(() => {
		const inputValue = getAPIUpdateNumber(editingValue);

		if (errorMessage) {
			_lastValidValue.current = getLastValidValue(getInitialEditingValue(value, dataTypeSettings, decimals), dataTypeSettings);
		} else {
			const divident = dataTypeSettings?.formatting === "percentage" ? 100 : 1;
			const finalValue = inputValue === null ? null : inputValue / divident; // if a user wants to delete the value and leave it empty, we need to send null (value / divident) becomes 0 instead of null

			_lastValidValue.current = finalValue;
		}
	}, [value, decimals, editingValue, errorMessage, dataTypeSettings]);

	const onInputEvent = (event: React.InputEvent<HTMLInputElement>) => {
		_hasTriggeredChange.current = false;
		setEditingValue(event.currentTarget.value);
	};

	const onClickInput = () => {
		setEditingValue(value?.toString());
		onClick?.();
	};

	const onKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
		let direction = 0;

		switch (event.key) {
			case KeyboardListener.KEY_DOWN:
				direction = -1;
				break;
			case KeyboardListener.KEY_UP:
				direction = 1;
				break;
			case KeyboardListener.KEY_ESCAPE:
				setEditingValue(value as unknown as string);
				_ref.current.blur();
				break;
			case KeyboardListener.KEY_ENTER:
				_ref.current.blur();
				break;
		}

		if (direction !== 0) {
			event.preventDefault();
			const stepSize = decimals >= 1 ? 0.1 : 1;
			const stepValue = direction * stepSize;
			const newValue = getEditingValueAsNumber(editingValue) + stepValue;

			setEditingValue(newValue.toFixed(decimals ?? 2));
		}
	};

	const triggerChange = useCallback(() => {
		if (_hasTriggeredChange.current) {
			return;
		}

		_hasTriggeredChange.current = true;

		if (value !== _lastValidValue.current) {
			onInput(_lastValidValue.current);
			onChange(_lastValidValue.current);
		}
	}, [value, onInput, onChange]);

	const triggerChangeRef = useRef(triggerChange);
	triggerChangeRef.current = triggerChange;

	useEffect(() => {
		return () => {
			triggerChangeRef.current();
		};
	}, []);

	const onBlurInput = () => {
		setEditingValue(FieldDataTypes.map[FieldDataType.Numeric].formatter(_lastValidValue.current, dataTypeSettings));
		triggerChange();
		onBlur?.();
	};

	useEffect(() => {
		const input = _ref.current;

		if (autoFocus) {
			input.focus();

			if (caretPosition !== undefined && input.setSelectionRange) {
				input.setSelectionRange(caretPosition, caretPosition);
			}
		}
	}, [autoFocus, caretPosition]);

	useEffect(() => {
		if (autoFocus) {
			_ref.current.focus();
		}
	}, [autoFocus]);

	useEffect(() => {
		setEditingValue(FieldDataTypes.map[FieldDataType.Numeric].formatter(value, dataTypeSettings));
	}, [value, dataTypeSettings]);

	return (
		<>
			<input
				ref={_ref}
				className={`numberInput ${className}`}
				value={editingValue}
				disabled={disabled}
				onBlur={onBlurInput}
				onInput={onInputEvent}
				onKeyUp={onKeyUp}
				onClick={onClickInput}
			/>
			{errorMessage && (
				<div
					ref={_errorIconRef}
					className="infoIcon editing"
					style={{position: "absolute", color: "red", top: "11px", right: "5px"}}
				>
					{className !== pageNumberClassName && (
						<div>
							<ErrorInfoIcon />
						</div>
					)}
					<InfoBubbleV5
						content={errorMessage}
						isErrorMessage={true}
						className={className}
						style={{zIndex: "9999"}}
						anchorRef={_errorIconRef}
						verticalAlignment={errorMessageVerticalAlignment}
						horizontalAlignment={errorMessageHorizontalAlignment}
						positionOffsetY={errorMessageOffsetY}
						positionOffsetX={errorMessageOffsetX}
					/>
				</div>
			)}
		</>
	);
};
